import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TaxSummaryWorkbookEditor from "@/components/despacho/TaxSummaryWorkbookEditor";
import { DespachoSectionCard } from "@/components/despacho/DespachoUi";
import api from "@/lib/api";
import { setStoredClientId, syncWorkClientFromDespacho } from "@/lib/clientSelection";
import {
  resolveTaxSummaryReturnLabel,
  resolveTaxSummaryReturnTarget,
  resolveTaxSummaryNavigateTarget,
  navigateTaxSummaryReturn,
} from "@/lib/taxSummaryNavigation";
import { buildTaxSummaryCapturePath } from "@/lib/taxSummaryWorkbookConstants";
import { yearFromPeriodKey } from "@/lib/taxSummaryWorkbookTitle";

const UNSAVED_PROMPT_MODE = {
  SWITCH: "switch",
  CLOSE: "close",
};

const CLOSED_UNSAVED_PROMPT = {
  open: false,
  mode: null,
  pendingClientId: "",
  pendingClientName: "",
};

function clientDisplayName(client) {
  if (!client) return "";
  return String(client.razon_social || client.nombre_comercial || client.rfc || "").trim();
}

function editorHasUnsavedChanges(editorRef) {
  return Boolean(
    editorRef.current?.hasUnsavedChanges?.()
    || editorRef.current?.isDirty?.(),
  );
}

export default function TaxSummaryCapturePage({ readOnly = false }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlClientId = String(searchParams.get("client") || "").trim();
  const yearParam = searchParams.get("year") || searchParams.get("period");
  const year = useMemo(() => {
    const fromPeriod = yearFromPeriodKey(yearParam);
    if (fromPeriod) return fromPeriod;
    const n = Number(yearParam);
    return Number.isFinite(n) && n >= 2000 ? n : new Date().getFullYear();
  }, [yearParam]);
  const urlClientName = String(searchParams.get("name") || "").trim();
  const returnTo = useMemo(
    () => resolveTaxSummaryReturnTarget(searchParams),
    [searchParams],
  );
  const returnToRef = useRef(returnTo);

  useEffect(() => {
    returnToRef.current = returnTo;
  }, [returnTo]);
  const returnLabel = useMemo(
    () => resolveTaxSummaryReturnLabel(
      returnTo,
      searchParams.get("returnLabel") || "",
    ),
    [returnTo, searchParams],
  );

  const editorRef = useRef(null);
  const dirtyRef = useRef(false);
  const activeClientIdRef = useRef(urlClientId);
  const activeClientNameRef = useRef(urlClientName);
  const prevUrlClientIdRef = useRef(urlClientId);
  const unsavedPromptOpenRef = useRef(false);
  const pendingClientRef = useRef("");

  const [clients, setClients] = useState([]);
  const [activeClientId, setActiveClientId] = useState(urlClientId);
  const [activeClientName, setActiveClientName] = useState(urlClientName);
  const [unsavedPrompt, setUnsavedPrompt] = useState(CLOSED_UNSAVED_PROMPT);
  const [promptActionLoading, setPromptActionLoading] = useState(false);

  const loadClients = useCallback(() => {
    api
      .get("/clients")
      .then(({ data }) => setClients(Array.isArray(data) ? data : []))
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const resolveClientName = useCallback((clientId, fallback = "") => {
    const match = clients.find((c) => c.id === clientId);
    return clientDisplayName(match) || fallback;
  }, [clients]);

  useEffect(() => {
    if (!activeClientId || activeClientName || !clients.length) return;
    const name = resolveClientName(activeClientId);
    if (name) setActiveClientName(name);
  }, [activeClientId, activeClientName, clients, resolveClientName]);

  useEffect(() => {
    activeClientIdRef.current = activeClientId;
  }, [activeClientId]);

  useEffect(() => {
    activeClientNameRef.current = activeClientName;
  }, [activeClientName]);

  useEffect(() => {
    unsavedPromptOpenRef.current = unsavedPrompt.open;
  }, [unsavedPrompt.open]);

  const buildBackTarget = useCallback((clientId = activeClientIdRef.current) => (
    resolveTaxSummaryNavigateTarget({
      returnTo: returnToRef.current,
      activeClientId: clientId,
      year,
    })
  ), [year]);

  const updateUrlForClient = useCallback((clientId, clientName) => {
    const params = new URLSearchParams();
    if (clientId) params.set("client", clientId);
    if (Number.isFinite(year) && year >= 2000) params.set("year", String(year));
    const name = String(clientName || "").trim();
    if (name) params.set("name", name);
    const backTarget = buildBackTarget(clientId);
    if (backTarget) params.set("returnTo", backTarget);
    returnToRef.current = backTarget;
    const label = String(searchParams.get("returnLabel") || "").trim();
    if (label) params.set("returnLabel", label);
    setSearchParams(params, { replace: true });
  }, [buildBackTarget, searchParams, setSearchParams, year]);

  const closeUnsavedPrompt = useCallback(() => {
    pendingClientRef.current = "";
    setUnsavedPrompt(CLOSED_UNSAVED_PROMPT);
  }, []);

  const applyClientSwitch = useCallback((clientId, clientName) => {
    const resolvedName = clientName || resolveClientName(clientId);
    pendingClientRef.current = "";
    dirtyRef.current = false;
    setActiveClientId(clientId);
    setActiveClientName(resolvedName);
    updateUrlForClient(clientId, resolvedName);
    setStoredClientId(clientId);
    setUnsavedPrompt(CLOSED_UNSAVED_PROMPT);
  }, [resolveClientName, updateUrlForClient]);

  const openSwitchPrompt = useCallback((pendingClientId, pendingClientName = "") => {
    const nextId = String(pendingClientId || "").trim();
    if (!nextId) return;
    if (unsavedPromptOpenRef.current && pendingClientRef.current === nextId) return;

    pendingClientRef.current = nextId;
    const resolvedPendingName = pendingClientName || resolveClientName(nextId);
    setUnsavedPrompt({
      open: true,
      mode: UNSAVED_PROMPT_MODE.SWITCH,
      pendingClientId: nextId,
      pendingClientName: resolvedPendingName,
    });
  }, [resolveClientName]);

  const hasPendingUnsavedChanges = useCallback(() => (
    dirtyRef.current || editorHasUnsavedChanges(editorRef)
  ), []);

  const requestClientSwitch = useCallback((pendingClientId, pendingClientName = "", { fromUrl = false } = {}) => {
    const nextId = String(pendingClientId || "").trim();
    const currentId = activeClientIdRef.current;
    if (!nextId || nextId === currentId) return;

    const resolvedPendingName = pendingClientName || resolveClientName(nextId);
    if (!hasPendingUnsavedChanges() || readOnly) {
      applyClientSwitch(nextId, resolvedPendingName);
      return;
    }

    if (fromUrl) {
      const currentName = activeClientNameRef.current || resolveClientName(currentId);
      updateUrlForClient(currentId, currentName);
    }
    openSwitchPrompt(nextId, resolvedPendingName);
  }, [
    applyClientSwitch,
    hasPendingUnsavedChanges,
    openSwitchPrompt,
    readOnly,
    resolveClientName,
    updateUrlForClient,
  ]);

  useEffect(() => {
    if (!urlClientId) {
      prevUrlClientIdRef.current = "";
      setActiveClientId("");
      setActiveClientName("");
      return;
    }
    if (urlClientId === activeClientIdRef.current) {
      prevUrlClientIdRef.current = urlClientId;
      if (!activeClientName && urlClientName) {
        setActiveClientName(urlClientName);
      }
      syncWorkClientFromDespacho(urlClientId);
      return;
    }
    if (urlClientId === prevUrlClientIdRef.current) return;
    prevUrlClientIdRef.current = urlClientId;
    if (unsavedPromptOpenRef.current) return;
    requestClientSwitch(urlClientId, urlClientName, { fromUrl: true });
  }, [urlClientId, urlClientName, requestClientSwitch]);

  useEffect(() => {
    if (!activeClientId) return;
    setStoredClientId(activeClientId);
  }, [activeClientId]);

  useEffect(() => {
    const onBeforeClientChange = (event) => {
      const nextId = String(event?.detail?.clientId || "").trim();
      const currentId = activeClientIdRef.current;
      if (!nextId || nextId === currentId) return;
      if (!hasPendingUnsavedChanges() || readOnly) return;
      event.preventDefault();
      openSwitchPrompt(nextId);
    };

    const onClientChanged = (event) => {
      const nextId = String(event?.detail?.clientId || "").trim();
      if (!nextId || nextId === activeClientIdRef.current) return;
      if (unsavedPromptOpenRef.current || pendingClientRef.current) return;
      requestClientSwitch(nextId);
    };

    window.addEventListener("visor:client-before-change", onBeforeClientChange);
    window.addEventListener("visor:client-changed", onClientChanged);
    return () => {
      window.removeEventListener("visor:client-before-change", onBeforeClientChange);
      window.removeEventListener("visor:client-changed", onClientChanged);
    };
  }, [hasPendingUnsavedChanges, openSwitchPrompt, readOnly, requestClientSwitch]);

  const handleDiscardAndSwitch = useCallback(async () => {
    const { pendingClientId, pendingClientName } = unsavedPrompt;
    if (!pendingClientId) {
      closeUnsavedPrompt();
      return;
    }
    setPromptActionLoading(true);
    try {
      setUnsavedPrompt((prev) => ({ ...prev, open: false }));
      await editorRef.current?.discardChanges?.();
      dirtyRef.current = false;
      applyClientSwitch(pendingClientId, pendingClientName);
    } finally {
      setPromptActionLoading(false);
    }
  }, [applyClientSwitch, closeUnsavedPrompt, unsavedPrompt]);

  const handleSaveAndSwitch = useCallback(async () => {
    const { pendingClientId, pendingClientName } = unsavedPrompt;
    if (!pendingClientId) {
      closeUnsavedPrompt();
      return;
    }
    setPromptActionLoading(true);
    try {
      const saved = await editorRef.current?.save?.();
      if (!saved) return;
      dirtyRef.current = false;
      setUnsavedPrompt((prev) => ({ ...prev, open: false }));
      applyClientSwitch(pendingClientId, pendingClientName);
    } finally {
      setPromptActionLoading(false);
    }
  }, [applyClientSwitch, closeUnsavedPrompt, unsavedPrompt]);

  const handleDirtyChange = useCallback((dirty) => {
    dirtyRef.current = dirty;
  }, []);

  const navigateBack = useCallback(() => {
    const clientId = activeClientIdRef.current;
    if (clientId) {
      syncWorkClientFromDespacho(clientId);
    }
    const target = buildBackTarget(clientId);
    navigateTaxSummaryReturn(navigate, target);
  }, [buildBackTarget, navigate]);

  const requestCloseTab = useCallback(() => {
    if (!hasPendingUnsavedChanges() || readOnly) {
      closeUnsavedPrompt();
      navigateBack();
      return;
    }
    setUnsavedPrompt({
      open: true,
      mode: UNSAVED_PROMPT_MODE.CLOSE,
      pendingClientId: "",
      pendingClientName: "",
    });
  }, [closeUnsavedPrompt, hasPendingUnsavedChanges, navigateBack, readOnly]);

  const handleDiscardAndClose = useCallback(() => {
    closeUnsavedPrompt();
    navigateBack();
  }, [closeUnsavedPrompt, navigateBack]);

  const handleSaveAndClose = useCallback(async () => {
    setPromptActionLoading(true);
    try {
      const saved = await editorRef.current?.save?.();
      if (!saved) return;
      closeUnsavedPrompt();
      navigateBack();
    } finally {
      setPromptActionLoading(false);
    }
  }, [closeUnsavedPrompt, navigateBack]);

  const currentClientLabel = activeClientName || resolveClientName(activeClientId) || "cliente actual";
  const pendingClientLabel = unsavedPrompt.pendingClientName
    || resolveClientName(unsavedPrompt.pendingClientId)
    || "otro cliente";
  const isSwitchPrompt = unsavedPrompt.mode === UNSAVED_PROMPT_MODE.SWITCH;

  const closeTabButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-8 text-xs border-[#0F2D5C]/40 text-[#0F2D5C] hover:bg-[#0F2D5C]/5"
      onClick={requestCloseTab}
      data-testid="tax-summary-close-tab"
      title="Cerrar esta captura y continuar con el trabajo"
    >
      <X className="h-3.5 w-3.5 mr-1.5" aria-hidden />
      Cerrar pestaña
    </Button>
  );

  return (
    <div className="space-y-4 p-1" data-testid="tax-summary-capture-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 text-xs text-[#0F2D5C] hover:bg-[#0F2D5C]/5"
          onClick={requestCloseTab}
          data-testid="tax-summary-back"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" aria-hidden />
          {returnLabel}
        </Button>
        {activeClientId ? closeTabButton : null}
      </div>

      {!activeClientId ? (
        <DespachoSectionCard title="Resumen Impuestos">
          <p className="text-sm text-slate-600 m-0">
            Falta el cliente. Abra esta pantalla desde el seguimiento fiscal mensual
            (botón «Resumen Impuestos») o agregue{" "}
            <code className="text-xs">?client=ID&amp;year=2026</code> en la URL.
          </p>
          {clients.length > 0 ? (
            <p className="text-xs text-slate-500 mt-2 mb-0">
              También puede elegir un cliente en la barra superior y volver aquí con{" "}
              <code className="text-xs">{buildTaxSummaryCapturePath({ fiscalClientId: "ID", year })}</code>.
            </p>
          ) : null}
        </DespachoSectionCard>
      ) : (
        <DespachoSectionCard
          title="Captura y cálculos"
          className="overflow-hidden"
        >
          <TaxSummaryWorkbookEditor
            ref={editorRef}
            key={`${activeClientId}-${year}`}
            fiscalClientId={activeClientId}
            clientName={activeClientName}
            year={year}
            readOnly={readOnly}
            onDirtyChange={handleDirtyChange}
          />
        </DespachoSectionCard>
      )}

      <AlertDialog
        open={unsavedPrompt.open}
        onOpenChange={(open) => {
          if (promptActionLoading) return;
          if (!open) closeUnsavedPrompt();
        }}
      >
        <AlertDialogContent
          data-testid={isSwitchPrompt
            ? "tax-summary-client-switch-dialog"
            : "tax-summary-close-tab-dialog"}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Cambios sin guardar</AlertDialogTitle>
            <AlertDialogDescription>
              {isSwitchPrompt ? (
                <>
                  Tiene cambios sin guardar en el resumen de{" "}
                  <strong>{currentClientLabel}</strong>. ¿Qué desea hacer antes de abrir{" "}
                  <strong>{pendingClientLabel}</strong>?
                </>
              ) : (
                <>
                  Tiene cambios sin guardar en el resumen de{" "}
                  <strong>{currentClientLabel}</strong>. ¿Qué desea hacer antes de cerrar
                  y continuar con el trabajo?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel disabled={promptActionLoading}>
              Cancelar
            </AlertDialogCancel>
            {isSwitchPrompt ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={promptActionLoading}
                  onClick={handleDiscardAndSwitch}
                  data-testid="tax-summary-client-switch-discard"
                >
                  Descartar cambios
                </Button>
                <Button
                  type="button"
                  className="bg-[#0F2D5C] hover:bg-[#0a2248]"
                  disabled={promptActionLoading}
                  onClick={handleSaveAndSwitch}
                  data-testid="tax-summary-client-switch-save"
                >
                  {promptActionLoading ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" aria-hidden />
                      Guardando…
                    </>
                  ) : (
                    "Guardar y continuar"
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={promptActionLoading}
                  onClick={handleDiscardAndClose}
                  data-testid="tax-summary-close-tab-discard"
                >
                  Descartar y cerrar
                </Button>
                <Button
                  type="button"
                  className="bg-[#0F2D5C] hover:bg-[#0a2248]"
                  disabled={promptActionLoading}
                  onClick={handleSaveAndClose}
                  data-testid="tax-summary-close-tab-save"
                >
                  {promptActionLoading ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" aria-hidden />
                      Guardando…
                    </>
                  ) : (
                    "Guardar y cerrar"
                  )}
                </Button>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
